import React, {
  useCallback,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
} from "react";

import { useGetIdentity, useNavigation } from "@refinedev/core";
import { useQuery } from "@tanstack/react-query";
import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";
import {
  Activity,
  ArrowRight,
  Bot,
  CircleDollarSign,
  Globe,
  HeartPulse,
  ListChecks,
  PlayCircle,
  RefreshCw,
  ShieldQuestion,
} from "lucide-react";
import {
  Area,
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  ComposedChart,
  Label,
  Line,
  Pie,
  PieChart,
  XAxis,
  YAxis,
} from "recharts";
import type { TooltipProps } from "recharts";

import { Badge } from "@/components/ui/badge";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  ChartConfig,
  ChartContext,
  ChartStyle,
  ChartTooltip,
  ChartTooltipContent,
} from "@/components/ui/chart";
import { Skeleton } from "@/components/ui/skeleton";
import { Toggle } from "@/components/ui/toggle";
import { isRefreshHitlReason } from "@/lib/tina/constants";
import { SPEND_CLASS_LABEL, type SpendClass } from "@/lib/tina/spend-class";
import { cn } from "@/lib/utils";
import {
  type CostTrendPoint,
  FIXED_COST_CATEGORIES,
  FIXED_COST_LABEL,
  type FixedCostCategory,
  aggregateCostTrend,
  enrichmentJobTotal,
  fixedCostTotals,
  spendByClass,
  spendClassesPresent,
} from "@/pages/tina/home/lib/cost-trend";
import { useMetricsRepository } from "@/pages/tina/hooks/use-metrics-repository";
import { formatUsdCost } from "@/pages/tina/lib/format-cost";
import {
  PERIOD_LABEL,
  PeriodChips,
  type PeriodKey,
  usePeriodRange,
} from "@/pages/tina/lib/period";
import type {
  CostDailyRow,
  EventsDailyRow,
  FixedCostDailyRow,
  HitlBacklogRow,
  OverviewRow,
  SiteQualityRow,
} from "@/types/tina";

import { TodayCoverageCard } from "./components/today-coverage";
import { WorldDominationRow } from "./components/world-domination";

dayjs.extend(relativeTime);

/* -------------------------------------------------------------------------- */
/* SizedChart — recharts ResponsiveContainer is broken on the alpha we pin.   */
/* -------------------------------------------------------------------------- */

function SizedChart({
  config,
  className,
  children,
}: {
  config: ChartConfig;
  className?: string;
  children: (size: { width: number; height: number }) => React.ReactNode;
}) {
  const uniqueId = useId();
  const chartId = `chart-${uniqueId.replace(/:/g, "")}`;
  const ref = useRef<HTMLDivElement>(null);
  const [size, setSize] = useState({ width: 0, height: 0 });

  const measure = useCallback(() => {
    if (ref.current) {
      const { width, height } = ref.current.getBoundingClientRect();
      setSize((prev) =>
        prev.width !== width || prev.height !== height
          ? { width, height }
          : prev,
      );
    }
  }, []);

  useEffect(() => {
    measure();
    const el = ref.current;
    if (!el) return;
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, [measure]);

  return (
    <ChartContext.Provider value={{ config }}>
      <div
        data-chart={chartId}
        ref={ref}
        className={cn(
          "text-xs [&_.recharts-cartesian-axis-tick_text]:fill-muted-foreground [&_.recharts-cartesian-grid_line[stroke='#ccc']]:stroke-border/50 [&_.recharts-curve.recharts-tooltip-cursor]:stroke-border [&_.recharts-dot[stroke='#fff']]:stroke-transparent [&_.recharts-layer]:outline-none [&_.recharts-polar-grid_[stroke='#ccc']]:stroke-border [&_.recharts-radial-bar-background-sector]:fill-muted [&_.recharts-rectangle.recharts-tooltip-cursor]:fill-muted [&_.recharts-reference-line_[stroke='#ccc']]:stroke-border [&_.recharts-sector[stroke='#fff']]:stroke-transparent [&_.recharts-sector]:outline-none [&_.recharts-surface]:outline-none",
          className,
        )}
      >
        <ChartStyle id={chartId} config={config} />
        {size.width > 0 && size.height > 0 && children(size)}
      </div>
    </ChartContext.Provider>
  );
}

/* -------------------------------------------------------------------------- */
/* Hero                                                                        */
/* -------------------------------------------------------------------------- */

function getGreeting(): string {
  const hour = dayjs().hour();
  if (hour < 12) return "Good morning";
  if (hour < 18) return "Good afternoon";
  return "Good evening";
}

interface HealthSignal {
  tone: "emerald" | "amber" | "red" | "zinc";
  label: string;
  hint: string;
}

/**
 * The hero's one-line health signal — two hardcoded thresholds over the
 * **scrape** aggregates.
 *
 * Both inputs are scrape-only and neither can currently be anything else:
 * `/insights/overview` has no `job_class` param, and `metric_hitl_backlog`
 * deliberately excludes `ENRICHMENT_HITL_REASONS` (enrichment parks get their
 * own surface). So this signal cannot see an enrichment incident, and says
 * "scrape" out loud rather than implying it covers the platform.
 *
 * The real systemic-failure detection is `/tina/health`, which reads
 * `/insights/errors` + `/insights/stages` — neither of which is class-filtered.
 */
function deriveHealth(
  overview: OverviewRow | undefined,
  backlog: HitlBacklogRow[] | undefined,
): HealthSignal {
  if (!overview) return { tone: "zinc", label: "Loading", hint: "—" };
  const total = overview.total_events || 0;
  const failed = overview.failed || 0;
  const failRate = total > 0 ? failed / total : 0;
  const pending = (backlog ?? []).reduce(
    (acc, r) => acc + (r.pending_count ?? 0),
    0,
  );
  if (failRate > 0.1)
    return {
      tone: "red",
      label: "Attention",
      hint: `${Math.round(failRate * 100)}% scrape failure rate`,
    };
  if (pending > 50)
    return {
      tone: "amber",
      label: "Backlog growing",
      hint: `${pending} scrape HITL pending`,
    };
  if (total === 0)
    return { tone: "zinc", label: "Quiet", hint: "no scraped events in range" };
  return {
    tone: "emerald",
    label: "Scraping healthy",
    hint: `${pending} HITL · ${Math.round(failRate * 100)}% failed`,
  };
}

const TONE_DOT: Record<HealthSignal["tone"], string> = {
  emerald: "bg-emerald-500",
  amber: "bg-amber-500",
  red: "bg-red-500",
  zinc: "bg-zinc-400",
};
const TONE_TEXT: Record<HealthSignal["tone"], string> = {
  emerald: "text-emerald-700 dark:text-emerald-300",
  amber: "text-amber-700 dark:text-amber-300",
  red: "text-red-700 dark:text-red-300",
  zinc: "text-zinc-700 dark:text-zinc-300",
};

function Hero({
  name,
  period,
  setPeriod,
  health,
}: {
  name: string;
  period: PeriodKey;
  setPeriod: (p: PeriodKey) => void;
  health: HealthSignal;
}) {
  return (
    <div className="relative overflow-hidden rounded-xl border bg-gradient-to-br from-indigo-500/10 via-sky-500/5 to-transparent">
      <div className="absolute -right-12 -top-12 h-48 w-48 rounded-full bg-indigo-500/10 blur-3xl" />
      <div className="absolute -right-4 -bottom-16 h-40 w-40 rounded-full bg-sky-500/10 blur-3xl" />
      <div className="relative z-10 flex flex-col gap-4 p-6 md:flex-row md:items-end md:justify-between">
        <div>
          <div className="inline-flex items-center gap-2 rounded-full border bg-background/60 px-2.5 py-0.5 text-[11px] font-medium backdrop-blur">
            <Bot className="h-3 w-3" />
            Tina · pipeline curator
          </div>
          <p className="mt-2 text-sm font-medium text-muted-foreground">
            {dayjs().format("dddd, MMMM D, YYYY")}
          </p>
          <h1 className="text-3xl font-bold tracking-tight">
            {getGreeting()}, {name}
          </h1>
          <div className="mt-2 inline-flex items-center gap-2 text-sm">
            <span
              className={cn(
                "inline-flex h-1.5 w-1.5 rounded-full",
                TONE_DOT[health.tone],
              )}
            />
            <span className={cn("font-medium", TONE_TEXT[health.tone])}>
              {health.label}
            </span>
            <span className="text-muted-foreground">· {health.hint}</span>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-[11px] uppercase tracking-[0.1em] text-muted-foreground">
            Window
          </span>
          <PeriodChips value={period} onChange={setPeriod} />
        </div>
      </div>
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/* KPI Card                                                                    */
/* -------------------------------------------------------------------------- */

type Tone = "sky" | "emerald" | "orange" | "red" | "indigo" | "zinc";
const TONE_CHIP: Record<Tone, string> = {
  sky: "bg-sky-500/10 text-sky-700 ring-sky-500/20 dark:text-sky-300",
  emerald:
    "bg-emerald-500/10 text-emerald-700 ring-emerald-500/20 dark:text-emerald-300",
  orange:
    "bg-orange-500/10 text-orange-700 ring-orange-500/20 dark:text-orange-300",
  red: "bg-red-500/10 text-red-700 ring-red-500/20 dark:text-red-300",
  indigo:
    "bg-indigo-500/10 text-indigo-700 ring-indigo-500/20 dark:text-indigo-300",
  zinc: "bg-zinc-500/10 text-zinc-700 ring-zinc-500/20 dark:text-zinc-300",
};

/* -------------------------------------------------------------------------- */
/* Outcome donut                                                               */
/* -------------------------------------------------------------------------- */

const OUTCOME_CONFIG: ChartConfig = {
  created: { label: "Created", color: "hsl(160 84% 39%)" },
  manual_review: { label: "Manual review", color: "hsl(28 96% 56%)" },
  duplicate: { label: "Duplicate", color: "hsl(240 5% 65%)" },
  skipped: { label: "Skipped", color: "hsl(240 5% 50%)" },
  failed: { label: "Failed", color: "hsl(0 84% 60%)" },
};

function OutcomeDonut({ overview }: { overview: OverviewRow | undefined }) {
  if (!overview) return <Skeleton className="h-[220px] w-full" />;
  const total = overview.total_events;
  const data = [
    { key: "created", value: overview.created },
    { key: "manual_review", value: overview.manual_review },
    { key: "duplicate", value: overview.duplicate },
    { key: "skipped", value: overview.skipped },
    { key: "failed", value: overview.failed },
  ].filter((d) => d.value > 0);

  const createdPct =
    total > 0 ? Math.round((overview.created / total) * 100) : 0;

  if (data.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center h-[220px] text-muted-foreground text-sm gap-1">
        <ListChecks className="h-5 w-5 opacity-60" />
        No events in window
      </div>
    );
  }

  return (
    <SizedChart config={OUTCOME_CONFIG} className="h-[220px] w-full mx-auto">
      {({ width, height }) => (
        <PieChart width={width} height={height}>
          <ChartTooltip
            cursor={false}
            content={<ChartTooltipContent hideLabel nameKey="key" />}
          />
          <Pie
            data={data}
            dataKey="value"
            nameKey="key"
            innerRadius={56}
            outerRadius={88}
            strokeWidth={4}
            paddingAngle={1}
          >
            {data.map((entry) => (
              <Cell
                key={entry.key}
                fill={`var(--color-${entry.key})`}
                stroke="hsl(var(--background))"
              />
            ))}
            <Label
              content={({ viewBox }) => {
                if (viewBox && "cx" in viewBox && "cy" in viewBox) {
                  return (
                    <text
                      x={viewBox.cx}
                      y={viewBox.cy}
                      textAnchor="middle"
                      dominantBaseline="middle"
                    >
                      <tspan
                        x={viewBox.cx}
                        y={(viewBox.cy ?? 0) - 6}
                        className="fill-foreground text-3xl font-bold"
                      >
                        {createdPct}%
                      </tspan>
                      <tspan
                        x={viewBox.cx}
                        y={(viewBox.cy ?? 0) + 16}
                        className="fill-muted-foreground text-[11px] uppercase tracking-[0.1em]"
                      >
                        created
                      </tspan>
                    </text>
                  );
                }
              }}
            />
          </Pie>
        </PieChart>
      )}
    </SizedChart>
  );
}

function OutcomeLegend({ overview }: { overview: OverviewRow | undefined }) {
  if (!overview) return null;
  const rows: { key: keyof typeof OUTCOME_CONFIG; value: number }[] = [
    { key: "created", value: overview.created },
    { key: "manual_review", value: overview.manual_review },
    { key: "duplicate", value: overview.duplicate },
    { key: "skipped", value: overview.skipped },
    { key: "failed", value: overview.failed },
  ];
  const total = overview.total_events;
  return (
    <div className="mt-2 grid grid-cols-2 gap-x-3 gap-y-1.5">
      {rows.map((r) => {
        const pct = total > 0 ? Math.round((r.value / total) * 100) : 0;
        return (
          <div
            key={r.key}
            className="flex items-center justify-between gap-2 text-xs"
          >
            <span className="flex items-center gap-1.5 min-w-0">
              <span
                className="h-2 w-2 rounded-full shrink-0"
                style={{ backgroundColor: `var(--color-${r.key})` }}
              />
              <span className="truncate text-muted-foreground">
                {OUTCOME_CONFIG[r.key].label as string}
              </span>
            </span>
            <span className="font-mono tabular-nums">
              {r.value} <span className="text-muted-foreground">· {pct}%</span>
            </span>
          </div>
        );
      })}
    </div>
  );
}

/**
 * The period's outcomes, plus the one fact the legend cannot carry.
 *
 * This card absorbed the old KPI strip. Three of that strip's four cards restated
 * what is already here — "Scraped events" was the donut's own total, "Created" and
 * "Failed" were legend rows with the same counts and the same percentages — so they
 * are gone. `HITL pending` is the one number that was NOT a duplicate: the legend's
 * "Manual review" counts outcomes that LANDED in review during the window, while
 * this counts the queue as it stands right now. The strip's scope notes ("scraped
 * only", the enrichment-job count) moved here with it, because without them the
 * donut's total and the enrichment number beside it answer different questions.
 */
function OutcomeCard({
  overview,
  period,
  pendingTotal,
  oldestBacklog,
  enrichmentJobs,
  onOpenHitl,
  className,
}: {
  overview: OverviewRow | undefined;
  period: PeriodKey;
  pendingTotal: number;
  oldestBacklog: string | null;
  enrichmentJobs: number;
  onOpenHitl: () => void;
  className?: string;
}) {
  return (
    <Card className={className}>
      <CardHeader className="pb-2">
        <CardTitle className="text-base flex items-center gap-2">
          <Activity className="h-4 w-4 text-muted-foreground" />
          Outcomes
        </CardTitle>
        <CardDescription>
          Where pipeline items landed · last {PERIOD_LABEL[period]} · scraped
          only
        </CardDescription>
      </CardHeader>
      <CardContent className="pb-4">
        <OutcomeDonut overview={overview} />
        <OutcomeLegend overview={overview} />
        <div className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-1 border-t pt-2 text-[11px] text-muted-foreground">
          <button
            type="button"
            onClick={onOpenHitl}
            className="inline-flex items-center gap-1.5 rounded transition-colors hover:text-foreground"
          >
            <ShieldQuestion className="h-3.5 w-3.5" />
            <span className="font-mono tabular-nums">
              {pendingTotal.toLocaleString()}
            </span>
            HITL pending
            {oldestBacklog && (
              <span>· oldest {dayjs(oldestBacklog).fromNow(true)}</span>
            )}
          </button>
          {enrichmentJobs > 0 && (
            <span>
              +{enrichmentJobs.toLocaleString()} enrichment jobs, not counted
              above
            </span>
          )}
        </div>
      </CardContent>
    </Card>
  );
}

/* -------------------------------------------------------------------------- */
/* Cost area chart                                                             */
/* -------------------------------------------------------------------------- */

function CostTooltip({
  active,
  payload,
  label,
  fixedShown = [],
}: TooltipProps<number, string> & {
  /** Fixed-cost series drawn right now; only those get a tooltip row. */
  fixedShown?: readonly FixedCostCategory[];
}) {
  if (!active || !payload || payload.length === 0) return null;
  const d = payload[0]?.payload as CostTrendPoint | undefined;
  if (!d) return null;
  const perEvent = d.events > 0 ? formatUsdCost(d.total / d.events) : "—";
  const dot = (color: string) => (
    <span
      className="inline-block h-2 w-2 rounded-full"
      style={{ background: color }}
    />
  );
  return (
    <div className="rounded-lg border bg-background px-3 py-2 text-xs shadow-md">
      <div className="mb-1 font-medium">
        {dayjs(label).format("ddd, MMM D")}
      </div>
      <div className="flex items-center justify-between gap-6 font-mono tabular-nums">
        <span className="flex items-center gap-1">
          {dot("var(--color-total)")}Total
        </span>
        <span className="font-semibold">{formatUsdCost(d.total)}</span>
      </div>
      <div className="flex items-center justify-between gap-6 font-mono tabular-nums">
        <span className="flex items-center gap-1">
          {dot("var(--color-llm)")}LLM
        </span>
        <span>{formatUsdCost(d.llm)}</span>
      </div>
      <div className="flex items-center justify-between gap-6 font-mono tabular-nums">
        <span className="flex items-center gap-1">
          {dot("var(--color-api)")}External API
        </span>
        <span>{formatUsdCost(d.api)}</span>
      </div>
      {fixedShown.includes("cloud_run") && (
        <div className="flex items-center justify-between gap-6 font-mono tabular-nums">
          <span className="flex items-center gap-1">
            {dot("var(--color-cloudRun)")}
            {FIXED_COST_LABEL.cloud_run}
          </span>
          <span>{formatUsdCost(d.cloudRun)}</span>
        </div>
      )}
      {fixedShown.includes("subscription") && (
        <div className="flex items-center justify-between gap-6 font-mono tabular-nums">
          <span className="flex items-center gap-1">
            {dot("var(--color-subscriptions)")}
            {FIXED_COST_LABEL.subscription}
          </span>
          <span>{formatUsdCost(d.subscriptions)}</span>
        </div>
      )}
      <div className="flex items-center justify-between gap-6 font-mono tabular-nums">
        <span className="flex items-center gap-1">
          {dot("var(--color-events)")}Scraped events
        </span>
        <span>{d.events.toLocaleString()}</span>
      </div>
      {d.enrichmentJobs > 0 && (
        <div className="flex items-center justify-between gap-6 font-mono tabular-nums">
          <span className="flex items-center gap-1">
            <span className="inline-block h-2 w-2" />
            Enrichment jobs
          </span>
          <span>{d.enrichmentJobs.toLocaleString()}</span>
        </div>
      )}
      <div className="mt-1 flex items-center justify-between gap-6 border-t pt-1 font-mono tabular-nums text-muted-foreground">
        <span>Cost / scraped event</span>
        <span>{perEvent}</span>
      </div>
    </div>
  );
}

/**
 * Spend classes enabled by default — music on, everything else off.
 *
 * Enrichment is deliberately a third **default-off pill** and not a stacked
 * series. Enrichment is projected
 * at the same order of magnitude as all scraping combined, so a chart that sums
 * the two by default stops answering "is the scraper getting more expensive?",
 * which is the question this card exists for.
 *
 * Default-off is not the same as invisible: every pill renders its own window
 * total, so the enrichment number is on screen whether or not it is in the
 * chart. See `spendByClass`.
 */
const DEFAULT_ON = new Set<SpendClass>(["music"]);

/** Chart `dataKey` of each fixed-cost series on `CostTrendPoint`. */
const FIXED_COST_KEY = {
  cloud_run: "cloudRun",
  subscription: "subscriptions",
} as const satisfies Record<FixedCostCategory, keyof CostTrendPoint>;

function CostTrend({
  rows,
  events,
  fixed,
}: {
  rows: CostDailyRow[];
  events: EventsDailyRow[];
  /** `/insights/fixed-cost` rows — `[]` when that request failed or is pending. */
  fixed: FixedCostDailyRow[];
}) {
  // Toggle which spend classes feed the chart. Music on, cinema/enrichment (etc.)
  // off by default; flip more on to see a class on its own or summed with others
  // (combined). `rows`/`events` arrive from `job_class=all` and are classified
  // here — see `lib/tina/spend-class.ts` for why the classifier tests `stage`.
  const [enabled, setEnabled] = useState<Partial<Record<SpendClass, boolean>>>(
    {},
  );
  const isEnabled = (cls: SpendClass) => enabled[cls] ?? DEFAULT_ON.has(cls);
  // Cloud Run and Subscriptions: their own pills, after the spend classes, ON by
  // default — the chart's total is what Tina costs, not only its per-call spend.
  const [fixedEnabled, setFixedEnabled] = useState<
    Partial<Record<FixedCostCategory, boolean>>
  >({});
  const isFixedEnabled = (cat: FixedCostCategory) => fixedEnabled[cat] ?? true;

  const available = useMemo(
    () => spendClassesPresent(rows, events),
    [rows, events],
  );
  const enabledSet = useMemo(
    () =>
      new Set(available.filter((cls) => enabled[cls] ?? DEFAULT_ON.has(cls))),
    [available, enabled],
  );
  // Only a category with rows in the window gets a pill, like a spend class.
  const fixedAvailable = useMemo(() => {
    const seen = new Set(fixed.map((f) => f.category));
    return FIXED_COST_CATEGORIES.filter((cat) => seen.has(cat));
  }, [fixed]);
  // The fixed series drawn right now — present in the window AND toggled on.
  const fixedShown = useMemo(
    () => fixedAvailable.filter((cat) => fixedEnabled[cat] ?? true),
    [fixedAvailable, fixedEnabled],
  );
  const fixedEnabledSet = useMemo(() => new Set(fixedShown), [fixedShown]);
  // Cost rows are per-(day, stage, content_type); aggregate to per-day over the
  // enabled spend classes, then merge event counts and the enabled fixed series.
  const data = useMemo(
    () => aggregateCostTrend(rows, events, enabledSet, fixed, fixedEnabledSet),
    [rows, events, enabledSet, fixed, fixedEnabledSet],
  );
  // Window totals for EVERY present class, including the toggled-off ones.
  const classTotals = useMemo(() => spendByClass(rows), [rows]);
  const fixedTotals = useMemo(() => fixedCostTotals(fixed), [fixed]);

  const totalSpend = data.reduce((acc, r) => acc + r.total, 0);
  const totalEvents = data.reduce((acc, r) => acc + r.events, 0);
  const totalJobs = data.reduce((acc, r) => acc + r.enrichmentJobs, 0);

  const toggleRow =
    available.length + fixedAvailable.length >= 2 ? (
      <div className="mb-2 flex flex-wrap items-center gap-1">
        {available.map((cls) => (
          <Toggle
            key={cls}
            size="sm"
            variant="outline"
            pressed={isEnabled(cls)}
            onPressedChange={(v) => setEnabled((e) => ({ ...e, [cls]: v }))}
            aria-label={`Toggle ${SPEND_CLASS_LABEL[cls]} — ${formatUsdCost(
              classTotals[cls],
            )} in window`}
            title={`${SPEND_CLASS_LABEL[cls]}: ${formatUsdCost(
              classTotals[cls],
            )} in this window`}
            className="h-6 gap-1.5 px-2 text-[11px]"
          >
            {SPEND_CLASS_LABEL[cls]}
            <span className="font-mono tabular-nums text-muted-foreground">
              {formatUsdCost(classTotals[cls])}
            </span>
          </Toggle>
        ))}
        {fixedAvailable.map((cat) => (
          <Toggle
            key={cat}
            size="sm"
            variant="outline"
            pressed={isFixedEnabled(cat)}
            onPressedChange={(v) =>
              setFixedEnabled((e) => ({ ...e, [cat]: v }))
            }
            aria-label={`Toggle ${FIXED_COST_LABEL[cat]} — ${formatUsdCost(
              fixedTotals[cat],
            )} in window`}
            title={`${FIXED_COST_LABEL[cat]}: ${formatUsdCost(
              fixedTotals[cat],
            )} in this window`}
            className="h-6 gap-1.5 px-2 text-[11px]"
          >
            {FIXED_COST_LABEL[cat]}
            <span className="font-mono tabular-nums text-muted-foreground">
              {formatUsdCost(fixedTotals[cat])}
            </span>
          </Toggle>
        ))}
      </div>
    ) : null;

  if (data.length === 0) {
    const hasAnyData = rows.length > 0 || events.length > 0 || fixed.length > 0;
    return (
      <>
        {toggleRow}
        <div className="flex flex-col items-center justify-center h-[200px] text-muted-foreground text-sm gap-1">
          <CircleDollarSign className="h-5 w-5 opacity-60" />
          {hasAnyData
            ? "No spend for the selected classes"
            : "No spend in window"}
        </div>
      </>
    );
  }

  const config: ChartConfig = {
    total: { label: "Total", color: "hsl(265 70% 60%)" },
    llm: { label: "LLM", color: "hsl(220 80% 55%)" },
    api: { label: "External API", color: "hsl(160 70% 42%)" },
    events: { label: "Scraped events", color: "hsl(38 92% 50%)" },
    // Hues with no status meaning: the Outcomes card owns red and grey, and
    // Total/LLM/API/events own purple, blue, green and amber.
    cloudRun: { label: FIXED_COST_LABEL.cloud_run, color: "hsl(187 75% 40%)" },
    subscriptions: {
      label: FIXED_COST_LABEL.subscription,
      color: "hsl(18 60% 45%)",
    },
  };

  const avgPerEvent =
    totalEvents > 0 ? formatUsdCost(totalSpend / totalEvents) : "—";

  return (
    <>
      {toggleRow}
      <div className="mb-1 flex items-baseline justify-between">
        <div>
          <div className="text-2xl font-bold tabular-nums">
            {formatUsdCost(totalSpend)}
          </div>
          <div className="text-[11px] text-muted-foreground">
            {data.length} day{data.length === 1 ? "" : "s"} ·{" "}
            {totalEvents.toLocaleString()} scraped events
            {totalJobs > 0
              ? ` · ${totalJobs.toLocaleString()} enrichment jobs`
              : ""}
          </div>
        </div>
        <div className="flex gap-1">
          <Badge
            variant="outline"
            className="text-[10px] font-mono tabular-nums"
          >
            avg {formatUsdCost(totalSpend / data.length)}/day
          </Badge>
          <Badge
            variant="outline"
            className="text-[10px] font-mono tabular-nums"
          >
            avg {avgPerEvent}/scraped event
          </Badge>
        </div>
      </div>
      <SizedChart config={config} className="h-[180px] w-full">
        {({ width, height }) => (
          <ComposedChart
            width={width}
            height={height}
            data={data}
            margin={{ top: 8, right: 8, left: 0, bottom: 0 }}
          >
            <defs>
              <linearGradient id="tina-total-fill" x1="0" y1="0" x2="0" y2="1">
                <stop
                  offset="5%"
                  stopColor="var(--color-total)"
                  stopOpacity={0.35}
                />
                <stop
                  offset="95%"
                  stopColor="var(--color-total)"
                  stopOpacity={0.02}
                />
              </linearGradient>
            </defs>
            <CartesianGrid vertical={false} strokeDasharray="3 3" />
            <XAxis
              dataKey="day"
              tickLine={false}
              axisLine={false}
              tickMargin={8}
              tickFormatter={(v) => dayjs(v).format("MMM D")}
              minTickGap={32}
            />
            <YAxis
              yAxisId="cost"
              tickLine={false}
              axisLine={false}
              tickMargin={4}
              width={56}
              tickFormatter={(v) => formatUsdCost(Number(v))}
            />
            <YAxis
              yAxisId="events"
              orientation="right"
              tickLine={false}
              axisLine={false}
              tickMargin={4}
              width={44}
              allowDecimals={false}
              tickFormatter={(v) => Number(v).toLocaleString()}
            />
            <ChartTooltip content={<CostTooltip fixedShown={fixedShown} />} />
            {/* Total is the filled envelope; LLM + API are drawn from zero (NOT
                stacked) so each line's height reflects its own cost. */}
            <Area
              yAxisId="cost"
              type="monotone"
              dataKey="total"
              stroke="var(--color-total)"
              fill="url(#tina-total-fill)"
              strokeWidth={2}
            />
            <Line
              yAxisId="cost"
              type="monotone"
              dataKey="llm"
              stroke="var(--color-llm)"
              strokeWidth={2}
              dot={false}
            />
            <Line
              yAxisId="cost"
              type="monotone"
              dataKey="api"
              stroke="var(--color-api)"
              strokeWidth={2}
              dot={false}
            />
            {/* Fixed cost: dashed, so a flat subscription line reads as fixed. */}
            {fixedShown.map((cat) => (
              <Line
                key={cat}
                yAxisId="cost"
                type="monotone"
                dataKey={FIXED_COST_KEY[cat]}
                stroke={`var(--color-${FIXED_COST_KEY[cat]})`}
                strokeWidth={2}
                strokeDasharray="4 3"
                dot={false}
              />
            ))}
            <Line
              yAxisId="events"
              type="monotone"
              dataKey="events"
              stroke="var(--color-events)"
              strokeWidth={2}
              dot={false}
            />
          </ComposedChart>
        )}
      </SizedChart>
    </>
  );
}

/* -------------------------------------------------------------------------- */
/* HITL backlog list                                                           */
/* -------------------------------------------------------------------------- */

/* -------------------------------------------------------------------------- */
/* Top sites bar chart                                                         */
/* -------------------------------------------------------------------------- */

// A site name is only readable whole: these carry their meaning at the END
// (`tickets/hu/budapest`, `posh/us-nyc`), so a clipped tail is worse than a short
// one. Recharts draws a category tick as plain SVG text — it neither wraps nor
// ellipsizes, it just overflows the axis and is cut by the chart's left edge. So
// the axis is sized to the longest label that will actually be drawn, and anything
// longer is ellipsized first. The tooltip still carries the full name.
// Deliberately generous: these are proportional glyphs and an estimate that runs
// short clips again, while one that runs long only spends axis space.
const SITE_LABEL_PX_PER_CHAR = 6.8;
const SITE_AXIS_MAX_PX = 230;
// The ellipsis budget falls OUT of the widest axis, so the two can never disagree
// — a label short enough to be drawn is exactly one the axis has room for. Sized
// against live data: the longest active music site on 2026-09-17 was 30 characters
// at the 95th percentile of 23, so nothing truncates today and this is the guard
// for the day a longer name appears rather than a thing the chart does.
const SITE_LABEL_MAX_CHARS = Math.floor(
  (SITE_AXIS_MAX_PX - 12) / SITE_LABEL_PX_PER_CHAR,
);

function shortSiteName(site: string): string {
  return site.length <= SITE_LABEL_MAX_CHARS
    ? site
    : `${site.slice(0, SITE_LABEL_MAX_CHARS - 1)}…`;
}

function siteAxisWidth(sites: string[]): number {
  const longest = sites.reduce(
    (max, s) => Math.max(max, shortSiteName(s).length),
    0,
  );
  return Math.min(
    SITE_AXIS_MAX_PX,
    Math.ceil(longest * SITE_LABEL_PX_PER_CHAR) + 12,
  );
}

function TopSitesChart({ rows }: { rows: SiteQualityRow[] }) {
  const top = useMemo(
    () =>
      [...rows]
        .sort((a, b) => b.event_count - a.event_count)
        .slice(0, 8)
        .map((r) => ({
          // The drawn name, so the axis and the tooltip always agree. `shortSiteName`
          // is a no-op at today's longest real name — see its constants.
          site: shortSiteName(r.site),
          created: r.created_count,
          hitl: r.hitl_count,
          rest:
            r.event_count - r.created_count - r.hitl_count > 0
              ? r.event_count - r.created_count - r.hitl_count
              : 0,
        })),
    [rows],
  );
  if (top.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center h-[260px] text-muted-foreground text-sm gap-1">
        <Globe className="h-5 w-5 opacity-60" />
        No site activity
      </div>
    );
  }
  const config: ChartConfig = {
    created: { label: "Created", color: "hsl(160 84% 39%)" },
    hitl: { label: "HITL", color: "hsl(28 96% 56%)" },
    rest: { label: "Other", color: "hsl(240 5% 75%)" },
  };
  return (
    <SizedChart config={config} className="h-[260px] w-full">
      {({ width, height }) => (
        <BarChart
          width={width}
          height={height}
          data={top}
          layout="vertical"
          margin={{ top: 4, right: 16, left: 0, bottom: 0 }}
          barCategoryGap={6}
        >
          <CartesianGrid horizontal={false} strokeDasharray="3 3" />
          <XAxis type="number" tickLine={false} axisLine={false} />
          <YAxis
            type="category"
            dataKey="site"
            tickLine={false}
            axisLine={false}
            width={siteAxisWidth(top.map((r) => r.site))}
            tick={{ fontSize: 11 }}
          />
          <ChartTooltip content={<ChartTooltipContent />} />
          <Bar
            dataKey="created"
            stackId="a"
            fill="var(--color-created)"
            radius={[4, 0, 0, 4]}
          />
          <Bar dataKey="hitl" stackId="a" fill="var(--color-hitl)" />
          <Bar
            dataKey="rest"
            stackId="a"
            fill="var(--color-rest)"
            radius={[0, 4, 4, 0]}
          />
        </BarChart>
      )}
    </SizedChart>
  );
}

/* -------------------------------------------------------------------------- */
/* Page                                                                        */
/* -------------------------------------------------------------------------- */

/** Stable empty value, so a failed fixed-cost read does not re-run the chart's memos. */
const NO_FIXED_COST: FixedCostDailyRow[] = [];

export const TinaHome = () => {
  const [period, setPeriod] = useState<PeriodKey>("30d");
  const { create, list } = useNavigation();
  const { data: identity } = useGetIdentity();
  const repo = useMetricsRepository();

  const range = usePeriodRange(period);

  const overviewQ = useQuery({
    queryKey: ["tina", "metrics", "overview", range.since, range.until],
    queryFn: () => repo.getOverview(range.since, range.until),
  });
  // `job_class=all` on both: the server default is `"scrape"`, which filters
  // every enrichment and embedding row out before IMS sees it. The chart's pills
  // are a filter over ONE aggregation, so it wants the rows and classifies them
  // client-side rather than issuing three requests.
  const costQ = useQuery({
    queryKey: ["tina", "metrics", "cost", range.since, range.until, "all"],
    queryFn: () => repo.getCost(range.since, range.until, undefined, "all"),
  });
  // Cloud Run + subscriptions, same window as `costQ`. Never awaited by the
  // chart: if it fails (IMS deployed before the Tina route) the chart still
  // draws the variable spend, it just has no fixed series.
  const fixedCostQ = useQuery({
    queryKey: ["tina", "metrics", "fixed-cost", range.since, range.until],
    queryFn: () => repo.getFixedCost(range.since, range.until),
  });
  const eventsQ = useQuery({
    queryKey: ["tina", "metrics", "events", range.since, range.until, "all"],
    queryFn: () => repo.getEvents(range.since, range.until, "all"),
  });
  const backlogQ = useQuery({
    queryKey: ["tina", "metrics", "hitl-backlog"],
    queryFn: () => repo.getHitlBacklog(),
  });
  // Music only. Unfiltered, this ranking is swamped by cinema — on 2026-09-17 six
  // cinema sites held more 7-day events than all 698 music sites together, so the
  // music sources the chart exists to show never appeared on it.
  const sitesQ = useQuery({
    queryKey: ["tina", "metrics", "sites", "music_event"],
    queryFn: () => repo.getSites("music_event"),
  });
  const overview = overviewQ.data;
  const cost = costQ.data ?? [];
  const events = eventsQ.data ?? [];
  const fixedCost = Array.isArray(fixedCostQ.data)
    ? fixedCostQ.data
    : NO_FIXED_COST;
  const backlog = backlogQ.data ?? [];
  const sites = sitesQ.data ?? [];

  // `metric_hitl_backlog` excludes the enrichment reasons (their backlog card used
  // to sit beside this one and now lives only on the enrichment page), but NOT
  // `refresh_review`, whose parks were ruled onto their own surface after that
  // metric shipped. Every number below points at `/tina/hitl`, so all of them
  // count what that queue lists; the refresh parks are counted once, next to the
  // link that opens them.
  const queueBacklog = backlog.filter(
    (r) => !isRefreshHitlReason(r.hitl_reason),
  );
  const refreshBacklogTotal = backlog.reduce(
    (acc, r) =>
      acc + (isRefreshHitlReason(r.hitl_reason) ? r.pending_count : 0),
    0,
  );
  const health = deriveHealth(overview, queueBacklog);
  const pendingTotal = queueBacklog.reduce(
    (acc, r) => acc + (r.pending_count ?? 0),
    0,
  );
  const oldestBacklog = queueBacklog.reduce<string | null>((acc, r) => {
    if (!r.oldest_at) return acc;
    if (!acc) return r.oldest_at;
    return r.oldest_at < acc ? r.oldest_at : acc;
  }, null);

  // Enrichment job rows in the window, straight off the `job_class=all` events
  // payload the chart already fetches. The KPI strip names them so a curator can
  // see that the headline is a *subset* of what Tina did.
  const enrichmentJobs = enrichmentJobTotal(events);

  const createdPct =
    overview && overview.total_events > 0
      ? Math.round((overview.created / overview.total_events) * 100)
      : null;
  const failedPct =
    overview && overview.total_events > 0
      ? Math.round((overview.failed / overview.total_events) * 100)
      : null;

  const greetingName =
    (identity as any)?.displayName?.split(" ")?.[0] ?? "curator";

  const quickActions: Array<{
    label: string;
    description: string;
    icon: React.ComponentType<{ className?: string }>;
    tone: Tone;
    onClick: () => void;
  }> = [
    {
      label: "Trigger run",
      description: "Kick off a manual run",
      icon: PlayCircle,
      tone: "indigo",
      onClick: () => create("tina-runs"),
    },
    {
      label: "HITL queue",
      description: `${pendingTotal} pending review`,
      icon: ShieldQuestion,
      tone: "orange",
      onClick: () => list("tina-hitl"),
    },
    {
      // Sits beside the HITL queue: both are queues waiting on a curator, and the
      // refresh parks are the ones `metric_hitl_backlog` deliberately excludes.
      label: "Refresh queue",
      description: `${refreshBacklogTotal} waiting`,
      icon: RefreshCw,
      tone: "sky",
      onClick: () => list("tina-refresh-review"),
    },
    {
      label: "Pipeline health",
      description: "Errors + stage latency",
      icon: HeartPulse,
      tone: "red",
      onClick: () => list("tina-health"),
    },
    {
      label: "Sites",
      description: "Manage sources",
      icon: Globe,
      tone: "emerald",
      onClick: () => list("tina-sites"),
    },
  ];

  return (
    <div className="flex flex-col gap-4 p-6 max-w-[1400px] mx-auto w-full">
      <Hero
        name={greetingName}
        period={period}
        setPeriod={setPeriod}
        health={health}
      />

      {/* Quick actions */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-2">
        {quickActions.map((a) => (
          <button
            key={a.label}
            type="button"
            onClick={a.onClick}
            className="group flex items-center gap-3 rounded-lg border bg-card p-3 text-left transition-all hover:bg-muted/40 hover:shadow-sm"
          >
            <div
              className={cn(
                "h-9 w-9 inline-flex items-center justify-center rounded-md ring-1 shrink-0",
                TONE_CHIP[a.tone],
              )}
            >
              <a.icon className="h-4 w-4" />
            </div>
            <div className="min-w-0 flex-1">
              <div className="text-sm font-medium">{a.label}</div>
              <div className="text-[11px] text-muted-foreground truncate">
                {a.description}
              </div>
            </div>
            <ArrowRight className="h-3.5 w-3.5 text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity" />
          </button>
        ))}
      </div>

      {/* The three cards below are what this page is FOR, so they lead it.

          What used to sit above them: a KPI strip whose three scrape cards
          restated the Outcomes donut (folded in — see OutcomeCard), an
          enrichment-review card, and a recent-runs card. The last two each cost a
          request and a full row; the enrichment backlog has its own page, and
          today's runs are one click from the coverage card. */}
      <TodayCoverageCard />

      {/* Spend over time + Outcomes — the period's two headline views. */}
      <div className="grid grid-cols-1 lg:grid-cols-5 gap-3">
        <Card className="lg:col-span-3">
          <CardHeader className="pb-2">
            <CardTitle className="text-base flex items-center gap-2">
              <CircleDollarSign className="h-4 w-4 text-muted-foreground" />
              Spend over time
            </CardTitle>
            <CardDescription>
              Daily total spend (LLM, external APIs, Cloud Run and
              subscriptions) vs scraped events · enrichment is its own pill, off
              by default
            </CardDescription>
          </CardHeader>
          <CardContent className="pb-4">
            <CostTrend rows={cost} events={events} fixed={fixedCost} />
          </CardContent>
        </Card>

        <OutcomeCard
          className="lg:col-span-2"
          overview={overview}
          period={period}
          pendingTotal={pendingTotal}
          oldestBacklog={oldestBacklog}
          enrichmentJobs={enrichmentJobs}
          onOpenHitl={() => list("tina-hitl")}
        />
      </div>

      {/* World domination */}
      <WorldDominationRow />

      {/* Top sites. The HITL backlog card used to share this row; it is gone — the
          queue's total is in the Outcomes card footer, the per-reason detail is
          `/tina/hitl`, and the refresh parks that card also counted have their own
          quick action above. */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-base flex items-center gap-2">
            <Globe className="h-4 w-4 text-muted-foreground" />
            Top sites by volume
          </CardTitle>
          <CardDescription>
            Created vs HITL by source · music sources · last 7 days
          </CardDescription>
        </CardHeader>
        <CardContent>
          {sitesQ.isLoading ? (
            <Skeleton className="h-[260px] w-full" />
          ) : (
            <TopSitesChart rows={sites} />
          )}
        </CardContent>
      </Card>
    </div>
  );
};
